import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from "react-toastify";

// GPS Coordinates & Location Parser Helper
const parseGpsLocation = (locationStr) => {
  if (!locationStr || typeof locationStr !== 'string') return null;
  const str = locationStr.trim();
  if (!str) return null;

  // Format 1: "Latitude:25.4452761, Longitude:84.8592364"
  const latLngRegex = /Latitude:\s*([-\d.]+),\s*Longitude:\s*([-\d.]+)/i;
  const match = str.match(latLngRegex);
  if (match) {
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return {
        isCoordinates: true,
        lat,
        lng,
        label: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        routeDestination: `${lat},${lng}`
      };
    }
  }

  // Format 2: "25.4452761, 84.8592364"
  const commaSplit = str.split(',');
  if (commaSplit.length === 2 && !isNaN(parseFloat(commaSplit[0])) && !isNaN(parseFloat(commaSplit[1]))) {
    const lat = parseFloat(commaSplit[0].replace(/[^\d.-]/g, ''));
    const lng = parseFloat(commaSplit[1].replace(/[^\d.-]/g, ''));
    if (!isNaN(lat) && !isNaN(lng)) {
      return {
        isCoordinates: true,
        lat,
        lng,
        label: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        routeDestination: `${lat},${lng}`
      };
    }
  }

  // Format 3: City or Address name e.g. "Gorakhpur"
  return {
    isCoordinates: false,
    lat: null,
    lng: null,
    label: str,
    routeDestination: str
  };
};

function Trades() {
  const authToken = sessionStorage.getItem('authToken');
  const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
  const baseurl = rawBaseUrl.trim();

  // Data States
  const [analytics, setAnalytics] = useState(null);
  const [tradesList, setTradesList] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
  const [isLoading, setIsLoading] = useState(false);

  // Filter States
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Selected Trade for Details & GPS Modal
  const [selectedTrade, setSelectedTrade] = useState(null);

  // Copy to clipboard helper
  const copyToClipboard = (text, label = 'Address') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const formatShortWallet = (wallet) => {
    if (!wallet) return 'N/A';
    if (wallet.length <= 14) return wallet;
    return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
  };

  // Open Google Maps Directions Route (live GPS navigation from current location to target)
  const openDefineRoute = (destination) => {
    if (!destination) {
      toast.warn("Location information unavailable for route navigation");
      return;
    }
    const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    window.open(routeUrl, '_blank', 'noopener,noreferrer');
  };

  // Fetch Trades from API
  const fetchTrades = useCallback(async (page = 1, search = '', status = 'all') => {
    setIsLoading(true);
    try {
      const url = `${baseurl}/trades?page=${page}&per_page=10&search=${encodeURIComponent(search)}&status=${encodeURIComponent(status)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status === 429) {
        toast.warn('Rate limit exceeded. Please wait a moment.');
        setIsLoading(false);
        return;
      }

      if (response.status === 401) {
        console.warn('API returned 401 Unauthenticated for trades.');
        setIsLoading(false);
        return;
      }

      const result = await response.json();

      if (result.status) {
        if (result.analytics) setAnalytics(result.analytics);
        if (Array.isArray(result.data)) setTradesList(result.data);
        if (result.pagination) setPagination(result.pagination);
      } else {
        toast.error(result.message || 'Failed to fetch trades');
      }
    } catch (error) {
      console.error('Fetch trades error:', error);
      toast.error('Network or server error while loading trades');
    } finally {
      setIsLoading(false);
    }
  }, [authToken, baseurl]);

  // Debounced Search Input
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== appliedSearch) {
        setAppliedSearch(trimmed);
        setCurrentPage(1);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [searchInput, appliedSearch]);

  // Fetch on page, search, or status change
  useEffect(() => {
    fetchTrades(currentPage, appliedSearch, statusFilter);
  }, [fetchTrades, currentPage, appliedSearch, statusFilter]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setAppliedSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination.last_page || 1)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Card filter click handler
  const handleCardFilterClick = (targetStatus) => {
    if (statusFilter === targetStatus) {
      setStatusFilter('all');
    } else {
      setStatusFilter(targetStatus);
    }
    setCurrentPage(1);
  };

  // Status Badge Helper
  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
          <i className="fa fa-check-circle me-1"></i> Active
        </span>
      );
    } else if (s === 'pending') {
      return (
        <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2 py-1">
          <i className="fa fa-clock me-1"></i> Pending
        </span>
      );
    } else if (s === 'closed') {
      return (
        <span className="badge bg-secondary-subtle text-secondary border px-2 py-1">
          <i className="fa fa-lock me-1"></i> Closed
        </span>
      );
    }
    return (
      <span className="badge bg-light text-muted border px-2 py-1">
        {status || 'Unknown'}
      </span>
    );
  };

  // Export current trades list to CSV
  const handleExportCSV = () => {
    if (!tradesList || tradesList.length === 0) {
      toast.info('No trades data to export');
      return;
    }
    const headers = [
      'Trade ID',
      'User ID',
      'User Name',
      'User Wallet',
      'Package',
      'Stake Amount',
      'Token',
      'Token Amount',
      'Status',
      'Total Income',
      'Direct Income',
      'Level Income',
      'Location / GPS',
      'Date'
    ];
    const rows = tradesList.map(t => [
      t.id,
      t.user_id,
      `"${(t.user_name || '').replace(/"/g, '""')}"`,
      `"${t.user_wallet || ''}"`,
      `"${t.package_name || ''}"`,
      t.stake_amount || 0,
      t.token_name || 'NMRC',
      t.token_amount || 0,
      t.stake_status || '',
      t.total_income || 0,
      t.direct_income || 0,
      t.level_income || 0,
      `"${(t.location || '').replace(/"/g, '""')}"`,
      t.joined || ''
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `trades_export_page_${pagination.current_page || 1}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Trades CSV downloaded successfully!');
  };

  const isFiltered = Boolean(searchInput || appliedSearch || statusFilter !== 'all');

  // Parsed location for modal
  const modalParsedGps = useMemo(() => {
    if (!selectedTrade) return null;
    return parseGpsLocation(selectedTrade.location || selectedTrade.txn_hash);
  }, [selectedTrade]);

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">

            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <h4 className="mb-1 text-dark fw-bold">Trade Stakes Management</h4>
                <p className="text-muted mb-0 fs-13">
                  Overview of locked trade packages, reward calculations, user investments, and geolocation audit tracking.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={handleExportCSV}
                  title="Export current table to CSV"
                >
                  <i className="fa fa-download"></i> Export CSV
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                  onClick={() => fetchTrades(currentPage, appliedSearch, statusFilter)}
                  title="Reload trade data"
                >
                  <i className="fa fa-sync-alt"></i> Refresh Data
                </button>
              </div>
            </div>

            {/* Top Interactive KPI Analytics Cards */}
            <div className="row g-3 mb-4">
              {/* Card 1: Total Trades */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div
                  className={`card overflow-hidden sales-card bg-primary-gradient border-0 shadow-sm h-100 mb-0 position-relative ${statusFilter === 'all' ? 'border border-2 border-white' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleCardFilterClick('all')}
                  title="Click to view all trades"
                  role="button"
                >
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">TOTAL TRADES</h6>
                      {statusFilter === 'all' && (
                        <span className="badge bg-white text-primary fs-10 px-1 py-0">ACTIVE</span>
                      )}
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(analytics?.total_stakes ?? pagination?.total ?? 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Volume: ${Number(analytics?.total_stake_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-layer-group text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Active Trades */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div
                  className={`card overflow-hidden sales-card bg-success-gradient border-0 shadow-sm h-100 mb-0 position-relative ${statusFilter === 'active' ? 'border border-2 border-white' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleCardFilterClick('active')}
                  title="Click to filter by active trades"
                  role="button"
                >
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">ACTIVE TRADES</h6>
                      {statusFilter === 'active' && (
                        <span className="badge bg-white text-success fs-10 px-1 py-0">FILTERED</span>
                      )}
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(analytics?.active_stakes || 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Income: ${Number(analytics?.total_income || 0).toFixed(2)}
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-check-circle text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Today's Trades */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div className="card overflow-hidden sales-card bg-warning-gradient border-0 shadow-sm h-100 mb-0">
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">TODAY'S TRADES</h6>
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(analytics?.today_stakes || 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Today Volume: ${Number(analytics?.today_stake_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-calendar-day text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Total Direct & Level Income */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div className="card overflow-hidden sales-card bg-danger-gradient border-0 shadow-sm h-100 mb-0">
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">REWARDS DISTRIBUTED</h6>
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {(Number(analytics?.total_direct_income || 0) + Number(analytics?.total_level_income || 0)).toFixed(2)}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Direct: {Number(analytics?.total_direct_income || 0).toFixed(2)} | Level: {Number(analytics?.total_level_income || 0).toFixed(2)}
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-coins text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Search Bar */}
            <div className="card border-0 shadow-sm mb-4 rounded-3">
              <div className="card-body p-3">
                <form onSubmit={handleSearchSubmit}>
                  <div className="row g-2 align-items-center">
                    {/* Search Field */}
                    <div className="col-lg-6 col-md-6 col-12">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white border-end-0 text-muted">
                          <i className="fa fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search by client name, user ID, wallet, or package..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                        />
                        {searchInput && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => {
                              setSearchInput('');
                              setAppliedSearch('');
                              setCurrentPage(1);
                            }}
                            title="Clear search"
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Status Dropdown */}
                    <div className="col-lg-3 col-md-3 col-6">
                      <select
                        className="form-select form-select-sm"
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="all">All Status ({Number(analytics?.total_stakes ?? pagination?.total ?? 0)})</option>
                        <option value="active">Active ({Number(analytics?.active_stakes || 0)})</option>
                        <option value="pending">Pending</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>

                    {/* Action Buttons */}
                    <div className="col-lg-3 col-md-3 col-6 d-flex gap-2 justify-content-end">
                      <button type="submit" className="btn btn-sm btn-primary flex-grow-1">
                        <i className="fa fa-filter me-1"></i> Filter
                      </button>
                      {isFiltered && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleResetFilters}
                          title="Reset all filters"
                        >
                          <i className="fa fa-undo me-1"></i> Reset
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Trades Table Card */}
            <div className="card card-body border-0 shadow-sm rounded-3 mb-4">
              <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <i className="fa fa-layer-group text-primary"></i>
                    <span>All Trade Stakes</span>
                  </h6>
                  {statusFilter !== 'all' && (
                    <span className="badge bg-light text-primary border text-capitalize">
                      Status: {statusFilter}
                    </span>
                  )}
                  {appliedSearch && (
                    <span className="badge bg-light text-muted border">
                      Search: "{appliedSearch}"
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-light text-muted border fs-12">
                    Total: {pagination.total || tradesList.length} Trades
                  </span>
                  <span className="badge bg-light text-muted border fs-12">
                    Page {pagination.current_page || 1} of {pagination.last_page || 1}
                  </span>
                </div>
              </div>

              <div className="table-responsive">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading trades...</span>
                    </div>
                    <p className="text-muted mt-2 fs-13">Loading trade stake records...</p>
                  </div>
                ) : tradesList.length > 0 ? (
                  <table className="table table-hover align-middle mb-0 text-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '70px' }}>ID</th>
                        <th>Client & User ID</th>
                        <th>Wallet Address</th>
                        <th>Package</th>
                        <th>Stake Amount</th>
                        <th>Token Asset</th>
                        <th>Total Income</th>
                        <th>Status</th>
                        <th>Location & Route</th>
                        <th>Staked Date</th>
                        <th className="text-end" style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradesList.map((trade) => {
                        const parsedLocation = parseGpsLocation(trade.location || trade.txn_hash);
                        return (
                          <tr key={trade.id}>
                            <td>
                              <span className="badge bg-light text-dark border font-monospace">
                                #{trade.id}
                              </span>
                            </td>

                            {/* USER NAME: Clickable redirect to User Details */}
                            <td>
                              <Link
                                to="/clientsDetails"
                                onClick={() => { sessionStorage.setItem("clientId", trade.user_id); }}
                                className="fw-semibold text-primary text-decoration-none d-block"
                                title="Click to view full user details profile"
                              >
                                {trade.user_name || 'Unnamed Client'} <i className="fa fa-external-link-alt fs-10 ms-1 opacity-50"></i>
                              </Link>
                              <span className="text-muted small fs-12">ID: #{trade.user_id}</span>
                            </td>

                            {/* WALLET ADDRESS */}
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <span className="font-monospace text-dark fs-13" title={trade.user_wallet}>
                                  {formatShortWallet(trade.user_wallet)}
                                </span>
                                {trade.user_wallet && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0 text-muted"
                                    onClick={() => copyToClipboard(trade.user_wallet, 'User Wallet')}
                                    title="Copy wallet address"
                                  >
                                    <i className="fa fa-copy fs-11"></i>
                                  </button>
                                )}
                              </div>
                            </td>

                            {/* PACKAGE NAME */}
                            <td>
                              <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
                                {trade.package_name || 'Lock Package'}
                              </span>
                            </td>

                            {/* STAKE AMOUNT */}
                            <td>
                              <span className="fw-bold text-dark">
                                ${Number(trade.stake_amount || 0).toLocaleString()}
                              </span>
                            </td>

                            {/* TOKEN ASSET */}
                            <td>
                              <div className="fw-semibold text-dark fs-13">
                                {trade.token_amount} {trade.token_name || 'NMRC'}
                              </div>
                            </td>

                            {/* TOTAL INCOME */}
                            <td>
                              <span className="fw-semibold text-success">
                                {Number(trade.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                              </span>
                            </td>

                            {/* STATUS BADGE */}
                            <td>
                              {getStatusBadge(trade.stake_status)}
                            </td>

                            {/* LOCATION & ROUTE BUTTON */}
                            <td>
                              {parsedLocation ? (
                                <div className="d-flex align-items-center gap-1">
                                  <span
                                    className="badge bg-light text-dark border fs-12"
                                    title={parsedLocation.label}
                                  >
                                    <i className="fa fa-map-marker-alt text-danger me-1"></i>
                                    {parsedLocation.label.length > 18
                                      ? `${parsedLocation.label.slice(0, 16)}...`
                                      : parsedLocation.label}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger p-1 py-0"
                                    onClick={() => openDefineRoute(parsedLocation.routeDestination)}
                                    title="Define route & get turn-by-turn directions"
                                  >
                                    <i className="fa fa-route"></i>
                                  </button>
                                </div>
                              ) : (
                                <span className="text-muted fs-12">N/A</span>
                              )}
                            </td>

                            {/* JOINED / STAKED DATE */}
                            <td>
                              <span className="text-muted fs-12">
                                {trade.joined || 'N/A'}
                              </span>
                            </td>

                            {/* ACTIONS: VIEW DETAILS & GPS MODAL */}
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                title="View Trade Details & GPS Route Map"
                                data-bs-toggle="modal"
                                data-bs-target="#tradeDetailsGpsModal"
                                onClick={() => setSelectedTrade(trade)}
                              >
                                <i className="fa fa-eye"></i> View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-5">
                    <div
                      className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center text-muted mb-3"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <i className="fa fa-layer-group fs-3"></i>
                    </div>
                    <h6 className="fw-semibold text-dark">No trade stakes found</h6>
                    <p className="text-muted fs-13 mb-3">
                      {isFiltered
                        ? 'No trades match your search query or status filter.'
                        : 'No trades recorded in the system.'}
                    </p>
                    {isFiltered && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleResetFilters}
                      >
                        <i className="fa fa-undo me-1"></i> Reset Filters
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* GridJS Style Pagination */}
              {pagination.total > 0 && (
                <div className="gridjs-footer border-top p-3">
                  <div className="gridjs-pagination d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div role="status" aria-live="polite" className="gridjs-summary fs-13 text-muted">
                      Showing <b>{(pagination.current_page - 1) * pagination.per_page + 1}</b> to{' '}
                      <b>{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</b> of{' '}
                      <b>{pagination.total}</b> results
                    </div>
                    <div className="gridjs-pages text-end">
                      {/* Previous Button */}
                      <button
                        title="Previous"
                        aria-label="Previous"
                        className={pagination.current_page === 1 ? "disabled" : ""}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                      >
                        Previous
                      </button>

                      {/* First Page */}
                      <button
                        className={pagination.current_page === 1 ? "gridjs-currentPage" : ""}
                        title="Page 1"
                        aria-label="Page 1"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>

                      {/* Ellipsis if needed */}
                      {pagination.current_page > 3 && <span>...</span>}

                      {/* Middle Pages */}
                      {Array.from({ length: 5 }, (_, i) => {
                        const page = pagination.current_page - 2 + i;
                        if (page > 1 && page < pagination.last_page) {
                          return (
                            <button
                              key={page}
                              className={pagination.current_page === page ? "gridjs-currentPage" : ""}
                              title={`Page ${page}`}
                              aria-label={`Page ${page}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        }
                        return null;
                      })}

                      {/* Ellipsis if needed */}
                      {pagination.current_page < pagination.last_page - 2 && <span>...</span>}

                      {/* Last Page */}
                      {pagination.last_page > 1 && (
                        <button
                          className={pagination.current_page === pagination.last_page ? "gridjs-currentPage" : ""}
                          title={`Page ${pagination.last_page}`}
                          aria-label={`Page ${pagination.last_page}`}
                          onClick={() => handlePageChange(pagination.last_page)}
                        >
                          {pagination.last_page}
                        </button>
                      )}

                      {/* Next Button */}
                      <button
                        title="Next"
                        aria-label="Next"
                        className={pagination.current_page === pagination.last_page ? "disabled" : ""}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* MODAL: TRADE DETAILS & LIVE GPS ROUTE MAP */}
        <div className="modal fade" id="tradeDetailsGpsModal" tabIndex="-1" aria-labelledby="tradeDetailsGpsModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header bg-light">
                <div>
                  <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" id="tradeDetailsGpsModalLabel">
                    <i className="fa fa-map-marked-alt text-danger"></i>
                    <span>Trade #{selectedTrade?.id} Details & GPS Location</span>
                  </h6>
                  <span className="text-muted fs-12">
                    Package: {selectedTrade?.package_name || 'Lock Package'} &bull; Status: {selectedTrade?.stake_status}
                  </span>
                </div>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div className="modal-body p-4">
                {selectedTrade ? (
                  <>
                    {/* Client & Identity Overview */}
                    <div className="p-3 bg-light rounded-3 mb-4">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                          <span className="text-muted fs-11 text-uppercase fw-semibold d-block">Client Name & Account</span>
                          <Link
                            to="/clientsDetails"
                            onClick={() => sessionStorage.setItem("clientId", selectedTrade.user_id)}
                            className="fs-16 fw-bold text-primary text-decoration-none"
                            data-bs-dismiss="modal"
                            title="Click to view full user profile"
                          >
                            {selectedTrade.user_name || 'Unnamed Client'} <i className="fa fa-external-link-alt fs-11 ms-1"></i>
                          </Link>
                          <span className="text-muted fs-12 ms-2">(User ID: #{selectedTrade.user_id})</span>
                        </div>
                        <div>
                          {getStatusBadge(selectedTrade.stake_status)}
                        </div>
                      </div>

                      <div className="row g-2 mt-2 pt-2 border-top">
                        <div className="col-md-7 col-12">
                          <span className="text-muted fs-11 text-uppercase d-block">User Wallet</span>
                          <div className="d-flex align-items-center gap-2">
                            <span className="font-monospace fs-12 text-dark">{selectedTrade.user_wallet}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-primary"
                              onClick={() => copyToClipboard(selectedTrade.user_wallet, 'User Wallet')}
                              title="Copy wallet address"
                            >
                              <i className="fa fa-copy"></i>
                            </button>
                          </div>
                        </div>
                        <div className="col-md-5 col-12">
                          <span className="text-muted fs-11 text-uppercase d-block">Staked Timestamp</span>
                          <span className="text-dark fs-12">{selectedTrade.joined || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Financial Metrics Cards */}
                    <h6 className="fw-bold fs-13 text-dark mb-2">Trade Balances & Returns</h6>
                    <div className="row g-2 mb-4 text-center">
                      <div className="col-md-3 col-6">
                        <div className="p-2 border rounded-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase d-block">Stake Amount</span>
                          <h6 className="fw-bold text-dark mb-0 mt-1">${Number(selectedTrade.stake_amount || 0).toLocaleString()}</h6>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="p-2 border rounded-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase d-block">Token Amount</span>
                          <h6 className="fw-bold text-primary mb-0 mt-1">{selectedTrade.token_amount} {selectedTrade.token_name || 'NMRC'}</h6>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="p-2 border rounded-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase d-block">Total Income</span>
                          <h6 className="fw-bold text-success mb-0 mt-1">${Number(selectedTrade.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</h6>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="p-2 border rounded-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase d-block">Direct Income</span>
                          <h6 className="fw-bold text-info mb-0 mt-1">${Number(selectedTrade.direct_income || 0).toFixed(2)}</h6>
                        </div>
                      </div>
                    </div>

                    {/* Geolocation, Map & Route Navigation */}
                    <div className="border rounded-3 p-3 bg-white shadow-xs">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold fs-13 text-dark mb-0 d-flex align-items-center gap-2">
                          <i className="fa fa-crosshairs text-danger"></i>
                          <span>Trade Transaction Geolocation</span>
                        </h6>
                        <span className="badge bg-light text-muted border">
                          Audit GPS
                        </span>
                      </div>

                      {modalParsedGps ? (
                        <>
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 p-2 bg-light rounded-2 mb-3">
                            <div>
                              <span className="text-muted fs-11 text-uppercase d-block fw-semibold">Recorded Location</span>
                              <span className="font-monospace fs-13 text-dark fw-bold">
                                {modalParsedGps.label}
                              </span>
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                onClick={() => copyToClipboard(modalParsedGps.label, 'Location Coordinates')}
                              >
                                <i className="fa fa-copy"></i> Copy
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                                onClick={() => openDefineRoute(modalParsedGps.routeDestination)}
                                title="Define live route & get directions in Google Maps"
                              >
                                <i className="fa fa-route"></i> Define Route
                              </button>
                            </div>
                          </div>

                          {/* Live Interactive Map Iframe Embed */}
                          <div className="rounded-3 overflow-hidden border" style={{ height: '300px' }}>
                            <iframe
                              title="Trade GPS Map"
                              width="100%"
                              height="100%"
                              frameBorder="0"
                              scrolling="no"
                              marginHeight="0"
                              marginWidth="0"
                              src={
                                modalParsedGps.isCoordinates
                                  ? `https://www.openstreetmap.org/export/embed.html?bbox=${modalParsedGps.lng - 0.015}%2C${modalParsedGps.lat - 0.015}%2C${modalParsedGps.lng + 0.015}%2C${modalParsedGps.lat + 0.015}&layer=mapnik&marker=${modalParsedGps.lat}%2C${modalParsedGps.lng}`
                                  : `https://maps.google.com/maps?q=${encodeURIComponent(modalParsedGps.label)}&output=embed`
                              }
                            ></iframe>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <i className="fa fa-map-marker-slash fs-24 mb-2 d-block"></i>
                          No GPS location recorded for this trade.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-muted">No trade selected.</p>
                )}
              </div>

              <div className="modal-footer bg-light d-flex justify-content-between">
                <button type="button" className="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">
                  Close
                </button>
                {selectedTrade && (
                  <div className="d-flex gap-2">
                    {modalParsedGps && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                        onClick={() => openDefineRoute(modalParsedGps.routeDestination)}
                      >
                        <i className="fa fa-directions"></i> Get Route Directions
                      </button>
                    )}
                    <Link
                      to="/clientsDetails"
                      onClick={() => sessionStorage.setItem("clientId", selectedTrade.user_id)}
                      className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                      data-bs-dismiss="modal"
                    >
                      <i className="fa fa-user me-1"></i> Open Full User Profile
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ToastContainer />
      </Header>
      <Footer />
    </>
  );
}

export default Trades;